import Spinner from '@/components/ui/spinner'
import { analytics } from '@/lib/analytics'
import {
  useGetUserQuery,
  useSessionAwareConnectedIntegrationsQuery,
  useSessionAwareOnboardingQuery,
} from '@/lib/features/user/userApi'
import { connectPipedreamIntegration, getPipedreamClient } from '@/lib/pipedream-auth'
import { SendMessageOptions } from '@/lib/type'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

type AppData = {
  name: string
  appIcon: string
}

type PipedreamSignInButtonProps = {
  appSlug: string
  sendMessage: (message: string, options?: SendMessageOptions) => Promise<void>
}

export function PipedreamSignInButton({ appSlug, sendMessage }: PipedreamSignInButtonProps) {
  const [appData, setAppData] = useState<AppData | null>(null)
  const [signingIntoPipedream, setSigningIntoPipedream] = useState(false)
  const [signedInAppSlugs, setSignedInAppSlugs] = useState<Set<string>>(new Set())
  const isSignedIn = signedInAppSlugs.has(appSlug)

  const { data: userData } = useGetUserQuery()
  const user = userData?.user
  const { data: connectedIntegrations } = useSessionAwareConnectedIntegrationsQuery()
  const { data: onboardingData } = useSessionAwareOnboardingQuery()

  const handlePipedreamSignIn = async () => {
    setSigningIntoPipedream(true)
    const accountId = await connectPipedreamIntegration(appSlug)
    setSigningIntoPipedream(false)

    if (!accountId) {
      toast.error('Failed to sign in to Pipedream')
      console.error('Failed to sign in to Pipedream')
      return
    }

    setSignedInAppSlugs((prev) => new Set([...prev, appSlug]))

    // Save OAuth tokens to database
    try {
      const saveResponse = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v1/pipedream/saveTokens`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accountId,
            appSlug,
          }),
        }
      )

      if (!saveResponse.ok) {
        console.error('Failed to save OAuth tokens')
        return
      }

      // Check if this is the first integration (activation)
      const isFirstIntegration =
        !connectedIntegrations?.providerIds || connectedIntegrations.providerIds.length === 0

      // Track integration connection from chat (Pipedream) - after successful token save
      analytics.copilot.integrationConnected(appSlug, 'chat')

      // Track activation if this is the first integration
      if (isFirstIntegration && onboardingData?.onboardingCompleted) {
        const onboardingCompletedAt = onboardingData.updatedAt
          ? new Date(onboardingData.updatedAt).getTime()
          : null
        const timeSinceOnboarding = onboardingCompletedAt
          ? Math.round((Date.now() - onboardingCompletedAt) / 1000)
          : 0
        analytics.activation.firstIntegrationConnected(appSlug, 'chat', timeSinceOnboarding)
      }
    } catch (error) {
      console.error('Error saving OAuth tokens:', error)
      return
    }

    // Notify copilot that Pipedream credentials are now available
    await sendMessage('', {
      integrationSignIn: {
        type: 'pipedream',
        appSlug: appSlug,
      },
    } satisfies SendMessageOptions)
    // Retrigger execute
  }

  useEffect(() => {
    const fetchAppDetails = async (abortController: AbortController) => {
      if (!user) {
        console.log('No user is available, skipping appSlugIcon fetch')
        return
      }

      console.log(`Fetching app icon for ${appSlug}`)

      const client = getPipedreamClient(user)

      if (!client) {
        return
      }

      const app = await client.getApp(appSlug).catch((e) => console.error(e))
      const appData = app?.data

      if (abortController.signal.aborted) {
        console.log('Fetch canceled')
        return
      }

      if (appData) {
        setAppData({
          name: appData.name,
          appIcon: appData.img_src,
        })
      } else {
        console.warn(`Could not find appSlug icon for app ${appSlug}`)
      }
    }

    const abortController = new AbortController()

    void fetchAppDetails(abortController)

    return () => {
      abortController.abort()
    }
  }, [appSlug, user])

  return (
    <div className="flex w-full gap-x-1">
      <div className="flex grow flex-col gap-y-2 overflow-hidden">
        <div className="mb-5 max-w-[80%] self-start">
          <div
            className="font-hanken-grotesk relative rounded-3xl bg-[#f5f5f7] px-4 py-3 text-[#2D1A47] shadow-sm"
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              fontFamily: 'Hanken Grotesk, sans-serif',
              width: 'fit-content',
              maxWidth: '100%',
              border: '1px solid #e1e1e5',
            }}
          >
            {isSignedIn ? (
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                  <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {appData && (
                  <>
                    <Image
                      src={appData.appIcon}
                      alt={`${appSlug} icon`}
                      width={20}
                      height={20}
                      className="h-5 w-5 rounded"
                    />
                    <span className="text-sm font-medium text-green-700">
                      Signed in to {appData?.name}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100">
                  <svg className="h-5 w-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {/* Animated Gradient Border Wrapper */}
                <motion.div
                  initial={{ backgroundPosition: '0% 0%' }}
                  animate={{ backgroundPosition: '200% 0%' }}
                  transition={{
                    duration: 2.5,
                    ease: 'linear',
                    repeat: Infinity,
                  }}
                  className="rounded-[18px] bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-[length:300%_300%] p-[2px]"
                >
                  {/* Inner Button */}
                  <motion.button
                    layout
                    className={cn(
                      `inline-flex items-center justify-center gap-2 bg-[#3f3f46]`,
                      `px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#2f2f34]`,
                      `focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6b7280] focus-visible:ring-offset-2`,
                      `active:scale-95 disabled:cursor-not-allowed disabled:opacity-50`,
                      `h-full w-full rounded-[16px]`
                    )}
                    onClick={handlePipedreamSignIn}
                    disabled={signingIntoPipedream}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={appData ? 'icon' : 'spinner'}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center"
                      >
                        {appData ? (
                          <Image
                            src={appData.appIcon}
                            alt={`${appSlug} icon`}
                            width={16}
                            height={16}
                            className="h-4 w-4 rounded"
                          />
                        ) : (
                          <Spinner variant="simple" color="#969696" size="sm" />
                        )}
                      </motion.div>
                    </AnimatePresence>
                    <AnimatePresence mode="wait">
                      {signingIntoPipedream ||
                        (appData && (
                          <motion.span
                            className={'text-base font-medium'}
                            key={signingIntoPipedream ? 'signing' : (appData?.name ?? 'empty')}
                            layout
                            initial={{ opacity: 0, y: 2 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -2 }}
                            transition={{ duration: 0.2 }}
                          >
                            {signingIntoPipedream ? 'Signing in...' : `Sign in to ${appData.name}`}
                          </motion.span>
                        ))}
                    </AnimatePresence>
                  </motion.button>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
